import type { EditablePath, EditorNode, EditorPoint, EditorSubpath } from './path-editor';

export type SmartOperation = 'sharp' | 'smooth' | 'sawtooth' | 'sawtooth-irregular' | 'circle' | 'straighten' | 'space';
export type SmartEditOptions = { /** Source document units, not screen pixels. */ amplitude?: number };
type Run = { nodes: EditorNode[]; closed: boolean; subpath: EditorSubpath };
type Patch = { point?: EditorPoint; inHandle?: EditorPoint | null; outHandle?: EditorPoint | null };
const add = (a: EditorPoint, b: EditorPoint): EditorPoint => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: EditorPoint, b: EditorPoint): EditorPoint => ({ x: a.x - b.x, y: a.y - b.y });
const mul = (a: EditorPoint, n: number): EditorPoint => ({ x: a.x * n, y: a.y * n });
const length = (a: EditorPoint) => Math.hypot(a.x, a.y);
const mix = (a: EditorPoint, b: EditorPoint, t: number) => add(a, mul(sub(b, a), t));
const unit = (a: EditorPoint) => mul(a, 1 / (length(a) || 1));
const key = (p: EditorPoint) => `${Math.round(p.x * 1e9)},${Math.round(p.y * 1e9)}`;
const same = (a?: EditorPoint | null, b?: EditorPoint | null) => !a || !b ? a == b : length(sub(a, b)) < 1e-10;
const edge = (a: EditorNode, b: EditorNode) => [a.point, a.outHandle ?? a.point, b.inHandle ?? b.point, b.point];
const edgeKey = (p: EditorPoint[]) => p.map(key).join(';');

function runs(paths: EditablePath[], selected: ReadonlySet<string>): Run[] {
  const result: Run[] = [];
  for (const path of paths) for (const subpath of path.subpaths) {
    const nodes = subpath.nodes, n = nodes.length; if (!n) continue;
    const included = nodes.map(node => selected.has(node.id));
    if (included.every(Boolean)) { result.push({ nodes, closed: subpath.closed, subpath }); continue; }
    // Start after an unselected node so a closed run crossing index zero is
    // contiguous; a gap always ends the current run.
    const start = subpath.closed ? (included.findIndex(value => !value) + 1) % n : 0;
    let current: EditorNode[] = [];
    for (let k = 0; k < n; k++) {
      const i = (start + k) % n;
      if (included[i]) current.push(nodes[i]);
      else if (current.length) { result.push({ nodes: current, closed: false, subpath }); current = []; }
    }
    if (current.length) result.push({ nodes: current, closed: false, subpath });
  }
  return result;
}

export function smartSelectionInfo(paths: EditablePath[], selected: ReadonlySet<string>) {
  const selectedRuns = runs(paths, selected);
  return { selectedCount: selectedRuns.reduce((n, run) => n + run.nodes.length, 0), runCount: selectedRuns.length,
    pairRuns: selectedRuns.filter(run => run.nodes.length >= 2).length, circleRuns: selectedRuns.filter(run => run.nodes.length >= 3).length,
    canApply: { sharp: selectedRuns.length > 0, smooth: selectedRuns.length > 0,
      sawtooth: selectedRuns.some(run => run.nodes.length >= 3), 'sawtooth-irregular': selectedRuns.some(run => run.nodes.length >= 3),
      circle: selectedRuns.some(run => circleGeometry(canonical(run)) !== null),
      straighten: selectedRuns.some(run => !run.closed && run.nodes.length >= 3), space: selectedRuns.some(run => run.nodes.length >= 3) } };
}

function canonical(run: Run): Run {
  let nodes = run.nodes.slice();
  if (run.closed) {
    const lowest = nodes.reduce((best, node, i) => key(node.point) < key(nodes[best].point) ? i : best, 0);
    nodes = [...nodes.slice(lowest), ...nodes.slice(0, lowest)];
    const reverse = [nodes[0], ...nodes.slice(1).reverse()];
    if (reverse.map(n => key(n.point)).join(';') < nodes.map(n => key(n.point)).join(';')) nodes = reverse;
  } else if (key(nodes[nodes.length - 1].point) < key(nodes[0].point)) nodes.reverse();
  return { ...run, nodes };
}

function circleFit(points: EditorPoint[]) {
  const origin = mul(points.reduce(add, { x: 0, y: 0 }), 1 / points.length);
  let xx = 0, xy = 0, yy = 0, xr = 0, yr = 0;
  for (const point of points) { const p = sub(point, origin), r = p.x * p.x + p.y * p.y; xx += p.x * p.x; xy += p.x * p.y; yy += p.y * p.y; xr += p.x * r; yr += p.y * r; }
  const determinant = xx * yy - xy * xy;
  if (Math.abs(determinant) < Math.max(1, xx * yy) * 1e-10) return null;
  const center = add(origin, { x: (xr * yy - yr * xy) / (2 * determinant), y: (yr * xx - xr * xy) / (2 * determinant) });
  const radius = points.reduce((sum, p) => sum + length(sub(p, center)), 0) / points.length;
  return radius > 1e-8 && Number.isFinite(radius) ? { center, radius } : null;
}

function circleGeometry(run: Run) {
  const nodes = run.nodes, n = nodes.length;
  if (n < 3) return null;
  const fit = circleFit(nodes.map(node => node.point)); if (!fit) return null;
  if (!run.closed) {
    const first = nodes[0].point, last = nodes[n - 1].point, chord = sub(last, first), midpoint = mix(first, last, .5), normal = unit({ x: -chord.y, y: chord.x });
    if (length(chord) < 1e-8) return null;
    const projection = sub(fit.center, midpoint); fit.center = add(midpoint, mul(normal, projection.x * normal.x + projection.y * normal.y));
    fit.radius = length(sub(first, fit.center));
  }
  const angles = nodes.map(node => Math.atan2(node.point.y - fit.center.y, node.point.x - fit.center.x));
  let turn = 0;
  for (let i = 1; i < n; i++) { let d = angles[i] - angles[i - 1]; while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI; turn += d; }
  const direction = turn < 0 ? -1 : 1, sweep = run.closed ? direction * Math.PI * 2 : turn;
  if (Math.abs(sweep) < 1e-7 || Math.abs(sweep) > 2 * Math.PI + 1e-6) return null;
  const step = sweep / (run.closed ? n : n - 1);
  if (Math.abs(step) > Math.PI * .99) return null;
  return { fit, angles, direction, step };
}

/** Local, deterministic geometry actions. Node ids/count and disjoint runs stay
 * intact. Exact source edge copies receive the same transformed controls. */
export function smartEditPaths(paths: EditablePath[], selected: ReadonlySet<string>, operation: SmartOperation, options: SmartEditOptions = {}): EditablePath[] {
  if (!selected.size) return paths;
  const positions = new Map<string, EditorPoint>(), direct = new Map<string, Patch>(), edgePatches = new Map<string, { start?: EditorPoint | null; end?: EditorPoint | null }>();
  const put = (node: EditorNode, patch: Patch) => direct.set(node.id, { ...direct.get(node.id), ...patch });
  const setPosition = (node: EditorNode, point: EditorPoint) => { positions.set(key(node.point), point); put(node, { point }); };
  const selectedRuns = runs(paths, selected), visited = new Set<string>();
  for (const original of selectedRuns) {
    const run = canonical(original), nodes = run.nodes, n = nodes.length;
    const nodeIndexes = new Map(run.subpath.nodes.map((node, index) => [node.id, index]));
    const signature = `${run.closed}:${nodes.map(node => key(node.point)).join(';')}`;
    // A coincident junction can have independent incident edges. Node-local
    // commands must visit every selected node even when coordinates match.
    if (operation !== 'sharp' && operation !== 'smooth') { if (visited.has(signature)) continue; visited.add(signature); }
    if (operation === 'sharp') { for (const node of nodes) put(node, { inHandle: null, outHandle: null }); continue; }
    if (operation === 'smooth') {
      for (const node of nodes) {
        const index = nodeIndexes.get(node.id)!, list = run.subpath.nodes, count = list.length;
        const before = index > 0 ? list[index - 1] : run.subpath.closed ? list[count - 1] : undefined;
        const after = index + 1 < count ? list[index + 1] : run.subpath.closed ? list[0] : undefined;
        if (!before && !after) continue;
        const tangent = unit(before && after ? sub(after.point, before.point) : before ? sub(node.point, before.point) : sub(after!.point, node.point));
        put(node, { inHandle: before ? sub(node.point, mul(tangent, length(sub(node.point, before.point)) / 3)) : null,
          outHandle: after ? add(node.point, mul(tangent, length(sub(after.point, node.point)) / 3)) : null });
      }
      continue;
    }
    if (n < 3 || operation === 'straighten' && run.closed) continue;
    if (operation === 'circle') {
      const geometry = circleGeometry(run); if (!geometry) continue;
      const { fit, angles, direction, step } = geometry;
      const firstIndex = nodeIndexes.get(nodes[0].id)!, secondIndex = nodeIndexes.get(nodes[1].id)!;
      // One run orientation applies to every anchor, including a partial arc
      // that crosses a closed subpath's index-zero seam.
      const forward = (firstIndex + 1) % run.subpath.nodes.length === secondIndex;
      const handle = fit.radius * 4 / 3 * Math.tan(Math.abs(step) / 4);
      for (let i = 0; i < n; i++) {
        const angle = angles[0] + step * i, radial = { x: Math.cos(angle), y: Math.sin(angle) }, p = !run.closed && (i === 0 || i === n - 1) ? nodes[i].point : add(fit.center, mul(radial, fit.radius)), tangent = { x: -radial.y * direction, y: radial.x * direction };
        setPosition(nodes[i], p);
        // Map canonical orientation back to the original node's in/out sides.
        const incoming = i > 0 || run.closed ? sub(p, mul(tangent, handle)) : undefined, outgoing = i < n - 1 || run.closed ? add(p, mul(tangent, handle)) : undefined;
        const patch: Patch = {};
        if (incoming) patch[forward ? 'inHandle' : 'outHandle'] = incoming;
        if (outgoing) patch[forward ? 'outHandle' : 'inHandle'] = outgoing;
        put(nodes[i], patch);
      }
      continue;
    }
    const points = nodes.map(node => node.point), distances = [0];
    for (let i = 1; i < n; i++) distances.push(distances[i - 1] + length(sub(points[i], points[i - 1])));
    if (run.closed) distances.push(distances[n - 1] + length(sub(points[0], points[n - 1])));
    const total = distances[distances.length - 1]; if (total < 1e-8) continue;
    const at = (distance: number) => { let i = 1; while (i < distances.length - 1 && distances[i] < distance) i++; return mix(points[i - 1], points[i % n], (distance - distances[i - 1]) / (distances[i] - distances[i - 1] || 1)); };
    for (let i = 0; i < n; i++) {
      let p: EditorPoint;
      if (operation === 'straighten') p = mix(points[0], points[n - 1], distances[i] / total);
      else {
        const distance = total * i / (run.closed ? n : n - 1); p = at(distance);
        if ((operation === 'sawtooth' || operation === 'sawtooth-irregular') && (run.closed || i > 0 && i < n - 1)) {
          const delta = sub(at(Math.min(total, distance + total / n * .1)), at(Math.max(0, distance - total / n * .1))), tangent = unit(delta);
          const jitter = operation === 'sawtooth-irregular' ? .55 + ((i * 1103515245 + 12345) >>> 0) % 1000 / 1000 * .9 : 1;
          const amplitude = Number.isFinite(options.amplitude) ? Math.max(0, options.amplitude!) : total / Math.max(1, n - 1) * .2;
          p = add(p, mul({ x: -tangent.y, y: tangent.x }, amplitude * (i % 2 ? 1 : -1) * jitter));
        }
      }
      setPosition(nodes[i], p);
      if (operation !== 'space') put(nodes[i], { inHandle: null, outHandle: null });
    }
  }
  // Control edits travel by their entire original edge, never by a coincident
  // cusp handle alone. This prevents unrelated sides at junctions from merging.
  for (const path of paths) for (const subpath of path.subpaths) for (let i = 0; i < subpath.nodes.length - (subpath.closed ? 0 : 1); i++) {
    const a = subpath.nodes[i], b = subpath.nodes[(i + 1) % subpath.nodes.length], pa = direct.get(a.id), pb = direct.get(b.id);
    if (pa?.outHandle === undefined && pb?.inHandle === undefined) continue;
    const controls = edge(a, b), patch = { start: pa?.outHandle, end: pb?.inHandle };
    edgePatches.set(edgeKey(controls), { ...edgePatches.get(edgeKey(controls)), ...Object.fromEntries(Object.entries(patch).filter(([, value]) => value !== undefined)) });
    edgePatches.set(edgeKey([...controls].reverse()), { ...edgePatches.get(edgeKey([...controls].reverse())), ...Object.fromEntries(Object.entries({ start: patch.end, end: patch.start }).filter(([, value]) => value !== undefined)) });
  }
  const linkedControl = new Map<string, Patch>();
  for (const path of paths) for (const subpath of path.subpaths) for (let i = 0; i < subpath.nodes.length - (subpath.closed ? 0 : 1); i++) {
    const a = subpath.nodes[i], b = subpath.nodes[(i + 1) % subpath.nodes.length], patch = edgePatches.get(edgeKey(edge(a, b))); if (!patch) continue;
    if (patch.start !== undefined) linkedControl.set(a.id, { ...linkedControl.get(a.id), outHandle: patch.start });
    if (patch.end !== undefined) linkedControl.set(b.id, { ...linkedControl.get(b.id), inHandle: patch.end });
  }
  let output: EditablePath[] | null = null;
  paths.forEach((path, pi) => {
    let subpaths: EditorSubpath[] | null = null;
    path.subpaths.forEach((subpath, si) => {
      let nodes: EditorNode[] | null = null;
      subpath.nodes.forEach((node, ni) => {
        const patch = { ...direct.get(node.id), ...linkedControl.get(node.id) }, p = positions.get(key(node.point)) ?? patch.point ?? node.point, delta = sub(p, node.point);
        const handles = (side: 'inHandle' | 'outHandle') => patch[side] === null ? undefined : patch[side] ?? (node[side] && add(node[side]!, delta));
        const incoming = handles('inHandle'), outgoing = handles('outHandle');
        if (same(p, node.point) && same(incoming, node.inHandle) && same(outgoing, node.outHandle)) return;
        nodes ??= subpath.nodes.slice(); nodes[ni] = { ...node, point: p, inHandle: incoming, outHandle: outgoing };
      });
      if (nodes) { subpaths ??= path.subpaths.slice(); subpaths[si] = { ...subpath, nodes }; }
    });
    if (subpaths) { output ??= paths.slice(); output[pi] = { ...path, subpaths }; }
  });
  return output ?? paths;
}
